import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  InputAdornment,
  Grid,
  Typography,
  Stack,
  FormGroup,
  FormControlLabel,
  Checkbox,
  useTheme,
  useMediaQuery,
  Box,
  IconButton,
  Chip,
  Avatar,
} from '@mui/material';
import * as consts from '@utilities/consts';
import {
  outputCentsToDollars,
  inputDollarsToCents,
  decimalFormatter,
  getMatchCount,
} from '@utilities/tools';
import { DefaultDonation, type Donation, type Match } from '@types';
import { Close as CloseIcon } from '@mui/icons-material';

export interface DonationDialogProps {
  charityId: string;
  initiativeId: string;
  matches: Match[];
  donationType: string;
  open: boolean;
  onClose: () => void;
}

//I want the program to be able to choose these
//I want the program to be able to include helper text that tells the user what their donation could do
const QUICK_AMOUNTS = [25, 100, 250, 500];
const QUICK_HELP: Record<number, string> = {
  25: 'Covers a week of lunches for a student',
  100: 'Funds a hygiene kit bundle',
  250: 'Supports family groceries for a week',
  500: 'Kickstarts a small community project',
};

export function DonationDialog({
  charityId,
  initiativeId,
  matches,
  donationType,
  open,
  onClose,
}: DonationDialogProps): React.JSX.Element {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [displayValue, setDisplayValue] = useState<string>(outputCentsToDollars(0));
  const [busy, setBusy] = useState<boolean>(true);
  const [donations, setDonations] = useState<Donation[]>([]);
  const [donation, setDonation] = useState<Donation>({
    ...DefaultDonation,
    charityId: charityId,
    initiativeId: initiativeId,
    donationType: donationType,
  });

  const matchCount = useMemo(() => {
    return getMatchCount(matches);
  }, [matches]);

  useEffect(() => {
    const fetchData = async () => {
      setBusy(true);
      let storageDonations = localStorage.getItem(consts.STORAGE_DONATIONS);
      if (storageDonations) {
        let existingDonations: Donation[] = JSON.parse(storageDonations);
        let existingDonation = existingDonations.find(
          (x) => x.charityId === donation.charityId && x.initiativeId === donation.initiativeId,
        );
        if (existingDonation) {
          setDonation(existingDonation);
          setDisplayValue(outputCentsToDollars(existingDonation.amountMinor));
          setDonations(existingDonations);
        } else {
          setDisplayValue(outputCentsToDollars(donation.amountMinor));
          setDonations([...existingDonations, donation]);
        }
      } else {
        setDonations([donation]);
      }
      setBusy(false);
    };
    fetchData();
  }, []);

  const navigateToCheckout = () => {
    window.location.href = `${consts.URL_CHECKOUT}`;
  };

  const handleQuickPick = (amount: number) => {
    setDonation((d) => ({ ...d, amountMinor: Math.round(amount * 100) }));
    setDisplayValue(decimalFormatter.format(amount));
  };

  return (
    <Dialog
      open={open}
      fullScreen={fullScreen}
      onClose={() => {
        onClose();
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
        }}
      >
        <Typography variant="h6">Add a Donation to Your Cart</Typography>
        <IconButton aria-label="Close dialog" onClick={onClose} edge="end">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }} container>
            <Grid size={{ xs: 12 }}>
              <Typography variant="body1" component="div" sx={{ fontWeight: 'bold' }}>
                Donation Amount
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                sx={{ mt: 1, mb: 1, flexWrap: 'wrap' }}
                aria-label="Quick donation amounts"
              >
                {QUICK_AMOUNTS.map((amt) => {
                  const isSelected = donation.amountMinor === Math.round(amt * 100);
                  return (
                    <Button
                      key={amt}
                      size="small"
                      variant={isSelected ? 'contained' : 'outlined'}
                      onClick={() => handleQuickPick(amt)}
                    >
                      ${amt.toLocaleString()}
                    </Button>
                  );
                })}
              </Stack>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                value={displayValue}
                helperText={donation.amountMinor < 2500 ? `Donation must be at least $25` : ``}
                placeholder="25"
                inputMode="numeric"
                slotProps={{
                  input: {
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    'aria-label': 'Donation amount',
                  },
                }}
                onChange={(event) => {
                  const cents = inputDollarsToCents(event.target.value);
                  setDonation((d) => ({ ...d, amountMinor: cents }));
                  setDisplayValue(outputCentsToDollars(cents));
                }}
              />
            </Grid>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography variant="body1" component="div" sx={{ fontWeight: 'bold' }}>
              Message
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={5}
              value={donation.message}
              onChange={(event) => {
                setDonation({ ...donation, message: event.target.value });
              }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={donation.optIn}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setDonation({
                        ...donation,
                        optIn: event.target.checked,
                      });
                    }}
                  />
                }
                label="Share my name and email address with this organization.​​"
              />
            </FormGroup>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Button
              variant="contained"
              fullWidth={true}
              disabled={donation.amountMinor < 2500}
              onClick={async () => {
                const globalMatch = matches.find((m) => m.categories.length === 0);
                const focusedMatch = matches.find((m) => m.categories.length > 0);
                let matchIds = [globalMatch?.id, focusedMatch?.id].filter(
                  (id): id is string => !!id,
                );
                let updatedDonations = [...donations];
                updatedDonations.map((updatedDonation) => {
                  if (
                    updatedDonation.charityId === donation.charityId &&
                    updatedDonation.initiativeId === donation.initiativeId
                  ) {
                    updatedDonation.amountMinor = donation.amountMinor;
                    updatedDonation.message = donation.message;
                    updatedDonation.optIn = donation.optIn;
                    updatedDonation.matchIds = matchIds;
                  }
                });
                localStorage.setItem(consts.STORAGE_DONATIONS, JSON.stringify(updatedDonations));
                navigateToCheckout();
              }}
            >
              Donate
            </Button>
          </Grid>
          <Grid size={{ xs: 12 }}>
            {matchCount > 0 ? (
              matchCount > 1 ? (
                <Chip
                  label="Double Match Available"
                  size="small"
                  icon={
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        bgcolor: '#218A58',
                        color: '#FFFFFF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 13,
                        lineHeight: 1,
                        ml: '5px !important',
                      }}
                    >
                      $
                    </Box>
                  }
                  sx={{
                    height: 26,
                    bgcolor: '#D9EDE2',
                    color: '#218A58',
                    border: '1px solid #218A58',
                    borderRadius: '13px',
                    fontSize: 12,
                    '& .MuiChip-label': {
                      pl: '8px',
                      pr: '9px',
                    },
                    '& .MuiChip-icon': {
                      color: '#FFFFFF',
                    },
                  }}
                />
              ) : (
                <Chip
                  label="Match Available"
                  size="small"
                  icon={
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        bgcolor: '#218A58',
                        color: '#FFFFFF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 13,
                        lineHeight: 1,
                        ml: '5px !important',
                      }}
                    >
                      $
                    </Box>
                  }
                  sx={{
                    height: 26,
                    bgcolor: '#D9EDE2',
                    color: '#218A58',
                    border: '1px solid #218A58',
                    borderRadius: '13px',
                    fontSize: 12,
                    '& .MuiChip-label': {
                      pl: '8px',
                      pr: '9px',
                    },
                    '& .MuiChip-icon': {
                      color: '#FFFFFF',
                    },
                  }}
                />
              )
            ) : null}
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}
